import React, { FC, ReactElement, memo, useCallback, useState, useEffect } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Modal } from 'shared/ui/Modal';
import { TextArea } from 'shared/ui/TextArea';
import { InputNumber } from 'shared/ui/InputNumber';
import { Button } from 'shared/ui/Button';
import { showNotification } from 'app/providers/NotificationsProvider';
import { useMarkDepositAsRefundedMutation } from 'entities/Deposit';
import { useCheckPermission } from 'shared/utils/hooks/useCheckPermission';
import { UserPermissions } from 'entities/User';
import { Form } from 'antd';
import { WarningPing } from 'shared/ui/WarningPing';
import { useGetCurrencySymbol } from 'app/appState';

interface MarkDepositAsRefundedProps {
  depositId: string;
  cancelledDepositAmount: number;
  action: ReactElement;
}

export const MarkDepositAsRefunded: FC<MarkDepositAsRefundedProps> = memo((props) => {
  const { depositId, cancelledDepositAmount, action } = props;

  const [isOpenedModal, setOpenedModal] = useState(false);
  const [form] = Form.useForm();
  const currencySymbol = useGetCurrencySymbol();

  const isForbidden = useCheckPermission(UserPermissions.REFUND_DEPOSITS);
  const isDisabled = isForbidden || cancelledDepositAmount === 0;

  const { t } = useAppTranslation(['contracts', 'common']);

  const [markDepositAsRefunded, { isLoading }] = useMarkDepositAsRefundedMutation();

  const openModal = useCallback((): void => {
    if (cancelledDepositAmount > 0) {
      setOpenedModal(true);
    }
  }, [cancelledDepositAmount]);

  const closeModal = useCallback((): void => {
    setOpenedModal(false);
    form.resetFields();
  }, [form]);

  useEffect(() => {
    if (cancelledDepositAmount) {
      form.setFieldValue('refundAmount', cancelledDepositAmount);
    }
  }, [cancelledDepositAmount, form]);

  const handleRefundDeposit = useCallback(
    async (data: { refundAmount: number; description?: string }): Promise<void> => {
      try {
        if (depositId) {
          await markDepositAsRefunded({
            depositId,
            refundAmount: data.refundAmount,
            description: data.description,
          }).unwrap();
          showNotification('info', t('Success', { ns: 'common' }), t('Deposit successfully marked as refunded', { ns: 'common' }));
        }
      } catch (error) {
        showNotification('error', t('Error', { ns: 'common' }), t('Error while deposit mark refund request'));
        console.log('error', error);
      } finally {
        closeModal();
      }
    },
    [markDepositAsRefunded, closeModal, depositId, t],
  );

  return (
    <>
      {React.cloneElement(action, { onClick: openModal, disabled: isDisabled })}

      <Modal className="max-w-[400px]" isOpen={isOpenedModal} onClose={closeModal}>
        <Form form={form} layout="vertical" onFinish={handleRefundDeposit} className="flex flex-col" disabled={isForbidden}>
          <div className="mb-2">
            <WarningPing type="warning" />
          </div>
          <div className="font-semibold text-xl mb-3">{t('Mark deposit as refunded')}</div>

          <div className="font-semibold text-lg mb-2">
            {t('Cancelled deposit amount is {{amount}} {{currencySymbol}}', {
              amount: cancelledDepositAmount,
              currencySymbol,
            })}
          </div>

          <div className="text-primaryLight font-normal mb-4">{t('Are you sure you want to mark this deposit as refunded?')}</div>

          <div className="my-4">
            <Form.Item
              name="refundAmount"
              label={t('Refund amount')}
              rules={[
                { required: true, message: t('Refund amount is required') },
                {
                  type: 'number',
                  min: 0,
                  max: cancelledDepositAmount,
                  message: t('Refund amount must be between 0 and {{amount}}', { amount: cancelledDepositAmount }),
                },
              ]}
            >
              <InputNumber className="w-full" placeholder={t('Enter refund amount')} bordered />
            </Form.Item>

            <Form.Item name="description" label={t('Description')}>
              <TextArea className="w-full" placeholder={t('Enter description (optional)')} rows={3} bordered />
            </Form.Item>
          </div>

          <div className="flex justify-end gap-3">
            <Button theme="secondary" onClick={closeModal} isDisabled={isLoading} className="flex-1">
              {t('Cancel')}
            </Button>
            <Button type="submit" isLoading={isLoading} isDisabled={isForbidden}>
              {t('Save', { ns: 'common' })}
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
});
